
import { useEffect, useState, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';

const heroImages = [
  {
    url: 'https://public.readdy.ai/ai/img_res/f15914ca8655db5e7055ee92ea6d7361.jpg',
    alt: 'Successful home restoration celebration'
  },
  {
    url: 'https://readdy.ai/api/search-image?query=Professional%20disaster%20cleanup%20technicians%20working%20together%20as%20team%20in%20bright%20well%20lit%20residential%20space%20using%20modern%20restoration%20equipment%20dehumidifiers%20air%20movers%20positive%20teamwork%20atmosphere%20Utah%20mountains%20visible%20through%20window%20hopeful%20recovery%20scene&width=1920&height=1080&seq=hero103&orientation=landscape',
    alt: 'Professional restoration team at work'
  },
  {
    url: 'https://readdy.ai/api/search-image?query=Beautiful%20bright%20modern%20home%20interior%20after%20complete%20water%20damage%20restoration%20in%20Utah%20sunlight%20streaming%20through%20windows%20fresh%20clean%20walls%20new%20flooring%20happy%20family%20moving%20back%20in%20hopeful%20new%20beginning%20atmosphere%20warm%20inviting%20space&width=1920&height=1080&seq=hero102&orientation=landscape',
    alt: 'Restored home interior with sunlight'
  }
];

const serviceLinks = [
  { icon: 'ri-drop-line', text: 'Flood Cleanup', link: '/services/flood-cleanup' },
  { icon: 'ri-bug-line', text: 'Mold Removal', link: '/services/mold-removal' },
  { icon: 'ri-fire-line', text: 'Fire Damage', link: '/services/smoke-damage' },
  { icon: 'ri-windy-line', text: 'Storm Damage', link: '/services/storm-damage' },
  { icon: 'ri-water-flash-line', text: 'Sewage Cleanup', link: '/services/sewage-cleanup' },
  { icon: 'ri-mist-line', text: 'Odor Removal', link: '/services/odor-removal' },
];

const trustBadges = [
  { icon: 'ri-24-hours-line', text: '24/7 Emergency' },
  { icon: 'ri-verified-badge-line', text: 'IICRC Certified' },
  { icon: 'ri-award-line', text: 'BBB Accredited' },
  { icon: 'ri-star-line', text: '4.7-Star Google Rating' },
];

const customerAvatars = [
  'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20middle%20aged%20caucasian%20man%20smiling%20friendly%20face%20clean%20background%20warm%20lighting%20business%20casual%20attire%20trustworthy%20appearance&width=80&height=80&seq=avatar1&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20woman%20smiling%20friendly%20face%20clean%20background%20warm%20lighting%20business%20casual%20attire%20approachable%20appearance&width=80&height=80&seq=avatar2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20middle%20aged%20woman%20smiling%20friendly%20face%20clean%20background%20warm%20lighting%20business%20casual%20attire%20warm%20appearance&width=80&height=80&seq=avatar3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20man%20smiling%20friendly%20face%20clean%20background%20warm%20lighting%20business%20casual%20attire%20confident%20appearance&width=80&height=80&seq=avatar4&orientation=squarish',
];

const ServiceLink = memo(({ item }: { item: typeof serviceLinks[0] }) => (
  <Link
    to={item.link}
    className="bg-sky-600 rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center hover:bg-sky-700 hover:scale-105 transition-all duration-300 ease-out cursor-pointer shadow-lg relative overflow-hidden"
  >
    <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center mb-2 sm:mb-3">
      <i className={`${item.icon} text-2xl sm:text-4xl text-white`}></i>
    </div>
    <p className="text-white text-[11px] sm:text-sm text-center font-semibold leading-tight mb-2 sm:mb-3">
      {item.text}
    </p>
    <div className="bg-white/20 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full flex items-center gap-1">
      <span className="text-white text-[9px] sm:text-xs font-medium whitespace-nowrap">Learn More</span>
      <i className="ri-arrow-right-line text-white text-[10px] sm:text-xs"></i>
    </div>
  </Link>
));

ServiceLink.displayName = 'ServiceLink';

const TrustBadge = memo(({ item }: { item: typeof trustBadges[0] }) => (
  <div className="flex items-center gap-2 sm:gap-4 text-gray-700">
    <i className={`${item.icon} text-2xl sm:text-5xl text-sky-600`}></i>
    <span className="text-xs sm:text-lg font-semibold">{item.text}</span>
  </div>
));

TrustBadge.displayName = 'TrustBadge';

function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([true, false, false]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const nextIndex = (currentImageIndex + 1) % heroImages.length;
    if (!imagesLoaded[nextIndex]) {
      const img = new Image();
      img.src = heroImages[nextIndex].url;
      img.onload = () => {
        setImagesLoaded(prev => {
          const newState = [...prev];
          newState[nextIndex] = true;
          return newState;
        });
      };
    }
  }, [currentImageIndex, imagesLoaded]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleIndicatorClick = useCallback((index: number) => {
    setCurrentImageIndex(index);
  }, []);

  const scrollToContact = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => {
      (document.getElementById('name') as HTMLInputElement)?.focus();
    }, 500);
  }, []);

  return (
    <>
      <section id="home" className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover object-top"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={index === 0 ? "high" : "low"}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30"></div>
        </div>

        <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-1 sm:gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              className={`h-1 sm:h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentImageIndex
                  ? 'w-4 sm:w-8 bg-white'
                  : 'w-1 sm:w-2 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-20 sm:pb-24">
          <div className="max-w-2xl">
            <div
              className={`flex flex-wrap items-center gap-2 sm:gap-3 mb-6 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-lg sm:text-2xl text-amber-400"></i>
                ))}
              </div>
              <span className="text-white/90 text-sm sm:text-lg font-medium">
                4.7 (From 127+ <span className="inline-flex items-center"><span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC05]">o</span><span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span></span> Reviews)
              </span>
            </div>

            <h1
              className={`text-3xl sm:text-5xl md:text-7xl font-black text-white mb-4 sm:mb-6 leading-tight transition-all duration-1000 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              Disaster Cleanup
              <br />
              You Can <span className="text-sky-400">Trust</span>
            </h1>

            <p
              className={`text-sm sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 max-w-xl transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <i className="ri-checkbox-circle-fill text-sky-400 mr-2"></i>
              From Free Assessment to Complete Restoration, Hiring us is Easy, Fast and Done in Just a Few Simple Steps.
            </p>

            <div
              className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10 transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <a
                href="#contact"
                onClick={scrollToContact}
                className="w-full sm:w-auto text-center bg-sky-600 text-white px-5 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                Get Your Free Assessment
              </a>
              <a
                href="tel:3853406632"
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm text-white px-5 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 hover:bg-white/20 hover:scale-105 whitespace-nowrap cursor-pointer border border-white/30 flex items-center justify-center gap-2"
              >
                <i className="ri-phone-fill"></i>
                Call Now: (385) 340-6632
              </a>
            </div>

            <div
              className={`flex flex-wrap items-center gap-3 sm:gap-4 mb-6 transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center">
                <i className="ri-award-fill text-3xl sm:text-5xl text-amber-400"></i>
              </div>
              
              <div className="flex -space-x-2 sm:-space-x-3">
                {customerAvatars.map((avatar, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 sm:w-12 sm:h-12 rounded-full border-2 border-white overflow-hidden"
                  >
                    <img
                      src={avatar}
                      alt={`Satisfied customer ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              <div className="flex flex-col">
                <span className="text-xl sm:text-3xl font-bold text-white">500+</span>
                <span className="text-white/70 text-xs sm:text-sm">Satisfied Customers</span>
              </div>
            </div>

            <div
              className={`inline-block bg-sky-600 px-4 sm:px-8 py-2 sm:py-3 rounded-full transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <span className="text-white text-xs sm:text-base font-semibold">
                Serving Davis County &amp; Northern Utah
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-10 sm:py-16 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 lg:gap-12 mb-8 sm:mb-12">
            {trustBadges.map((item, index) => (
              <TrustBadge key={index} item={item} />
            ))}
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-5">
            {serviceLinks.map((item, index) => (
              <ServiceLink key={index} item={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default memo(Hero);
